import type {
  PostingSchedule,
  PostingSlot,
  PostingSlotInput,
  PostCategoryRef,
  SocialPlatform,
  UpdatePostingScheduleDto,
} from '@mg/shared';

/**
 * The Scheduler Settings editor's draft: every posting schedule of the workspace, edited side by side
 * and saved together ("Save schedule" writes them all in one request). Pure functions only, so each
 * decision has a test of its own; PostingScheduleView calls them.
 *
 * A schedule is named the way the server names it: a category's id, or `null` for General, the
 * catch-all that every post without a category uses.
 */
export type ScheduleId = string | null;

/** One weekly time as the editor holds it. `key` is the editor's own; the server's id is never sent back. */
export type EditableSlot = {
  key: string;
  dayOfWeek: number;
  minute: number;
  platform: SocialPlatform | null;
  enabled: boolean;
};

/** A category and its own weekly times. */
export interface DraftCategory {
  id: string;
  name: string;
  color: string;
  slots: EditableSlot[];
}

export interface ScheduleDraft {
  /** Shared by every schedule: the workspace's timezone. */
  timezone: string;
  /** General's times. */
  general: EditableSlot[];
  /** Each category with its times, in creation order (the order the server lists them in). */
  categories: DraftCategory[];
}

let keySeq = 0;
const nextKey = () => `slot-${keySeq++}`;

const toEditable = (s: PostingSlot): EditableSlot => ({
  key: nextKey(),
  dayOfWeek: s.dayOfWeek,
  minute: s.minute,
  platform: s.platform,
  enabled: s.enabled,
});

/**
 * The draft for what the server answered: General from `slots`, each category from `categories`. An
 * older API (mid-rollout) sends no `categories`, and the editor then holds General alone.
 */
export function hydrateDraft(schedule: PostingSchedule): ScheduleDraft {
  return {
    timezone: schedule.timezone,
    general: schedule.slots.map(toEditable),
    categories: (schedule.categories ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      color: c.color,
      slots: c.slots.map(toEditable),
    })),
  };
}

const toInput = (s: EditableSlot): PostingSlotInput => ({
  dayOfWeek: s.dayOfWeek,
  minute: s.minute,
  platform: s.platform,
  enabled: s.enabled,
});

/**
 * The one request "Save schedule" sends: General's times as `slots`, and every category in the draft
 * with its own times. The server replaces each listed schedule's times as a set; a category it is not
 * sent (one made elsewhere after this page loaded) keeps its times.
 *
 * With no categories the field is left out rather than sent empty. The two mean the same to this API,
 * and an older one (still serving mid-rollout) refuses a field it does not know.
 */
export function toDto(draft: ScheduleDraft): UpdatePostingScheduleDto {
  return {
    timezone: draft.timezone,
    slots: draft.general.map(toInput),
    ...(draft.categories.length > 0
      ? { categories: draft.categories.map((c) => ({ id: c.id, slots: c.slots.map(toInput) })) }
      : {}),
  };
}

/** How many of a schedule's times are switched on: the count the sidebar shows beside its name. */
export function enabledCount(slots: readonly EditableSlot[]): number {
  return slots.filter((s) => s.enabled).length;
}

/** A schedule's times: General's, or the category's (none for a category the draft does not have). */
export function slotsOf(draft: ScheduleDraft, id: ScheduleId): EditableSlot[] {
  if (id === null) return draft.general;
  return draft.categories.find((c) => c.id === id)?.slots ?? [];
}

/** The draft with one schedule's times changed and every other schedule left exactly as it was. */
function withSlots(
  draft: ScheduleDraft,
  id: ScheduleId,
  change: (slots: EditableSlot[]) => EditableSlot[],
): ScheduleDraft {
  if (id === null) return { ...draft, general: change(draft.general) };
  return {
    ...draft,
    categories: draft.categories.map((c) => (c.id === id ? { ...c, slots: change(c.slots) } : c)),
  };
}

/** A new time on a day of one schedule: 09:00, any platform, enabled (as the single schedule always did). */
export function addSlot(draft: ScheduleDraft, id: ScheduleId, dayOfWeek: number): ScheduleDraft {
  return withSlots(draft, id, (slots) => [
    ...slots,
    { key: nextKey(), dayOfWeek, minute: 9 * 60, platform: null, enabled: true },
  ]);
}

export function removeSlot(draft: ScheduleDraft, id: ScheduleId, key: string): ScheduleDraft {
  return withSlots(draft, id, (slots) => slots.filter((s) => s.key !== key));
}

export function patchSlot(
  draft: ScheduleDraft,
  id: ScheduleId,
  key: string,
  patch: Partial<Omit<EditableSlot, 'key'>>,
): ScheduleDraft {
  return withSlots(draft, id, (slots) => slots.map((s) => (s.key === key ? { ...s, ...patch } : s)));
}

/** A category just created: last in the list (creation order), with no times yet. Never twice. */
export function addCategory(draft: ScheduleDraft, category: PostCategoryRef): ScheduleDraft {
  if (draft.categories.some((c) => c.id === category.id)) return draft;
  return {
    ...draft,
    categories: [
      ...draft.categories,
      { id: category.id, name: category.name, color: category.color, slots: [] },
    ],
  };
}

/** A category renamed or recoloured. Its times, and its place in the list, stay as they are. */
export function renameCategory(draft: ScheduleDraft, category: PostCategoryRef): ScheduleDraft {
  return {
    ...draft,
    categories: draft.categories.map((c) =>
      c.id === category.id ? { ...c, name: category.name, color: category.color } : c,
    ),
  };
}

/** A category deleted: it goes, with its times. Every other schedule keeps its unsaved edits. */
export function removeCategory(draft: ScheduleDraft, id: string): ScheduleDraft {
  return { ...draft, categories: draft.categories.filter((c) => c.id !== id) };
}

/** The categories whose names hold what was typed, ignoring case; all of them for an empty search. */
export function filterCategories<T extends { name: string }>(categories: readonly T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  return q ? categories.filter((c) => c.name.toLowerCase().includes(q)) : [...categories];
}

/**
 * The category that already has this name, ignoring case and surrounding spaces, other than `exceptId`
 * (a category may be renamed to its own name in other letter case). The server holds names unique the
 * same way; this only says so before a request is made.
 */
export function categoryNamed<T extends { id: string; name: string }>(
  categories: readonly T[],
  name: string,
  exceptId?: string,
): T | undefined {
  const key = name.trim().toLowerCase();
  return categories.find((c) => c.id !== exceptId && c.name.trim().toLowerCase() === key);
}

/** The API's own words for a category named "General" (it refuses the name with a 400). */
export const RESERVED_NAME_ERROR = 'General is the name of the default schedule. Choose another name.';

/**
 * Whether a name is "General", ignoring case and surrounding spaces: the built-in schedule's name, which
 * no category may take (it would read exactly like General wherever posts show their schedule).
 */
export function isReservedName(name: string): boolean {
  return name.trim().toLowerCase() === 'general';
}
